/**
 * Client-side Session Refresh Hook
 * 
 * Ce hook vérifie périodiquement l'expiration de la session et la rafraîchit automatiquement.
 * À utiliser dans les composants Client qui nécessitent une session active.
 * 
 * Le hook:
 * - Vérifie l'expiration toutes les minutes
 * - Rafraîchit automatiquement si le token expire dans moins de 5 minutes
 * - Redirige vers /signin si le refresh échoue
 */

'use client'

import { useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'

interface UseSessionRefreshOptions {
  /** Interval de vérification en ms (défaut: 60000 = 1 minute) */
  checkInterval?: number
  
  /** Temps avant expiration pour déclencher le refresh en ms (défaut: 300000 = 5 minutes) */
  refreshThreshold?: number
  
  /** Callback appelé après un refresh réussi */
  onRefresh?: () => void
  
  /** Callback appelé si le refresh échoue */
  onRefreshError?: (error: Error) => void
}

/**
 * Hook pour gérer le refresh automatique de session côté client
 * 
 * @example
 * ```tsx
 * 'use client'
 * 
 * export default function ProtectedPage() {
 *   useSessionRefresh({
 *     onRefresh: () => console.log('Session refreshed'),
 *     onRefreshError: (err) => console.error('Refresh failed', err)
 *   })
 *   
 *   return <div>Protected content</div>
 * }
 * ```
 */
export function useSessionRefresh(options: UseSessionRefreshOptions = {}) {
  const {
    checkInterval = 60000, // 1 minute
    refreshThreshold = 300000, // 5 minutes
    onRefresh,
    onRefreshError,
  } = options
  
  const router = useRouter()
  const isRefreshing = useRef(false)
  
  useEffect(() => {
    const checkAndRefreshSession = async () => {
      // Éviter les refreshs simultanés
      if (isRefreshing.current) {
        return
      }
      
      try {
        // Appeler l'endpoint API pour vérifier et rafraîchir si nécessaire
        const response = await fetch('/api/auth/session-status', {
          method: 'GET',
          credentials: 'include',
        })
        
        if (!response.ok) {
          // Session invalide ou expirée sans refresh possible
          console.warn('[useSessionRefresh] Session invalid, redirecting to signin')
          router.push('/signin')
          return
        }
        
        const data = await response.json()
        
        // Vérifier si le token expire bientôt
        if (data.expiresIn && data.expiresIn < refreshThreshold) {
          console.log('[useSessionRefresh] Token expires soon, refreshing...', {
            expiresIn: Math.floor(data.expiresIn / 1000) + 's',
          })
          
          isRefreshing.current = true
          
          // Appeler l'endpoint de refresh
          const refreshResponse = await fetch('/api/auth/refresh-session', {
            method: 'POST',
            credentials: 'include',
          })
          
          isRefreshing.current = false
          
          if (!refreshResponse.ok) {
            const error = new Error('Session refresh failed')
            console.error('[useSessionRefresh] Refresh failed, redirecting to signin')
            onRefreshError?.(error)
            router.push('/signin')
            return
          }
          
          console.log('[useSessionRefresh] ✅ Session refreshed successfully')
          onRefresh?.()
        }
      } catch (error) {
        isRefreshing.current = false
        const err = error instanceof Error ? error : new Error('Unknown error')
        console.error('[useSessionRefresh] Error:', err)
        onRefreshError?.(err)
      }
    }
    
    // Vérifier immédiatement au montage
    checkAndRefreshSession()
    
    // Puis vérifier périodiquement
    const intervalId = setInterval(checkAndRefreshSession, checkInterval)
    
    return () => {
      clearInterval(intervalId)
    }
  }, [checkInterval, refreshThreshold, router, onRefresh, onRefreshError])
}
